// ===================================================================
// data/miniGamesEn/index.ts
// English version of the cultural quizzes (miniGamesData.ts).
// The French data stays the source of truth (answers are checked on the
// French strings, challenges between friends store French text). English
// is only applied at display time, looked up by question id / text.
// Any question without a translation falls back to French.
// ===================================================================

import { MINI_GAMES_DATA, QuizQuestion } from '../miniGamesData';
import { QuizEn } from './types';
import { YORUBA_EN } from './yoruba';
import { SWAHILI_EN } from './swahili';
import { COPTE_EN } from './copte';
import { KAMA_EN } from './kama';
import { PEUL_AUTRES_PAYS_EN } from './peulAutresPays';
import { PEUL_EN } from './peul';
import { BAMBARA_EN } from './bambara';
import { WOLOF_EN } from './wolof';
import { LINGALA_EN } from './lingala';

export type { QuizEn } from './types';

const EN: Record<string, QuizEn> = {
  ...YORUBA_EN,
  ...SWAHILI_EN,
  ...COPTE_EN,
  ...KAMA_EN,
  ...PEUL_AUTRES_PAYS_EN,
  ...PEUL_EN,
  ...BAMBARA_EN,
  ...WOLOF_EN,
  ...LINGALA_EN,
};

const COUNTRY_NAME_EN: Record<string, string> = {
  bambara: 'Mali',
  wolof: 'Senegal',
  lingala: 'DR Congo',
  swahili: 'Kenya / Tanzania',
  peul: 'Guinea',
  peul_niger: 'Niger',
  peul_nigeria: 'Nigeria',
  peul_cameroun: 'Cameroon (Fulani)',
  copte: 'Egypt',
  kama: 'Egypt',
  yoruba: 'Nigeria (Yorubaland)',
};

interface Entry { orig: QuizQuestion; en: QuizEn }

let byQuestion: Map<string, Entry> | null = null;

// Built lazily: only complete translations (same number of options) are kept.
function index(): Map<string, Entry> {
  if (byQuestion) return byQuestion;
  byQuestion = new Map();
  for (const country of Object.values(MINI_GAMES_DATA)) {
    for (const questions of Object.values(country.themes)) {
      for (const q of questions ?? []) {
        const en = EN[q.id];
        if (en && en.o.length === q.options.length) byQuestion.set(q.question, { orig: q, en });
      }
    }
  }
  return byQuestion;
}

export const isEnglish = (lang?: string): boolean => !!lang && lang.toLowerCase().startsWith('en');

/** Question text in the UI language (French question as key). */
export function quizQuestionText(questionFr: string, lang?: string): string {
  if (!isEnglish(lang)) return questionFr;
  return index().get(questionFr)?.en.q ?? questionFr;
}

/** Option label in the UI language (matched by its position in the original French options). */
export function quizOptionText(questionFr: string, optionFr: string, lang?: string): string {
  if (!isEnglish(lang)) return optionFr;
  const entry = index().get(questionFr);
  if (!entry) return optionFr;
  const i = entry.orig.options.indexOf(optionFr);
  return i >= 0 ? entry.en.o[i] : optionFr;
}

/** Explanation in the UI language. */
export function quizExplanationText(questionFr: string, explanationFr: string, lang?: string): string {
  if (!isEnglish(lang)) return explanationFr;
  return index().get(questionFr)?.en.e ?? explanationFr;
}

/** Country name in the UI language. */
export function quizCountryName(dataKey: string, countryNameFr: string, lang?: string): string {
  if (!isEnglish(lang)) return countryNameFr;
  return COUNTRY_NAME_EN[dataKey] ?? countryNameFr;
}
